import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faSort } from "@fortawesome/free-solid-svg-icons";
import {
  formatDateParts,
  humanizeToken,
  actorDisplayName,
  targetDisplayName,
  actorTypeLabel,
  actionLabel,
  targetTypeLabel,
  actorTypeBadgeClass,
  actionBadgeClass,
  targetTypeBadgeClass,
  metadataEntries,
} from "./activityLogUtils";

const TH_CLASS =
  "border-b border-slate-200 px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wide text-slate-600";
const BADGE_CLASS =
  "inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold";

const ActivityLogTable = ({ logs, isLoading, sort, onSortChange, onView }) => {
  const toggleSort = () => onSortChange(sort === "newest" ? "oldest" : "newest");

  return (
    <div className="overflow-x-auto">
      <table className="admin-table-responsive w-full border-collapse text-[13px]">
        <thead>
          <tr className="bg-slate-50/70">
            <th className={TH_CLASS}>
              <button
                type="button"
                onClick={toggleSort}
                className="inline-flex items-center gap-1 font-semibold uppercase tracking-wide text-slate-600"
              >
                Date
                <FontAwesomeIcon
                  icon={faSort}
                  className={`h-3 w-3 ${sort === "oldest" ? "rotate-180" : ""}`}
                />
              </button>
            </th>
            <th className={TH_CLASS}>Actor</th>
            <th className={TH_CLASS}>Action</th>
            <th className={TH_CLASS}>Target</th>
            <th className={TH_CLASS}>Details</th>
            <th className={`${TH_CLASS} text-center`}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && (
            <tr>
              <td colSpan={6}>
                <div className="flex justify-center py-8">
                  <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-200 border-t-[#16213A]" />
                </div>
              </td>
            </tr>
          )}

          {!isLoading && logs.length === 0 && (
            <tr>
              <td colSpan={6}>
                <div className="flex justify-center py-10 text-sm text-slate-500">
                  No activity log entries to display.
                </div>
              </td>
            </tr>
          )}

          {!isLoading &&
            logs.map((log, index) => {
              const timestamp = log.createdAt || log.created_at || log.timestamp;
              const { date, time } = formatDateParts(timestamp);
              const actorName = actorDisplayName(log);
              const targetName = targetDisplayName(log);
              const details = metadataEntries(log);

              return (
                <tr
                  key={log.id ?? log._id ?? index}
                  className="border-b border-slate-100 align-top transition hover:bg-slate-50"
                >
                  <td className="whitespace-nowrap px-3 py-3">
                    <div className="font-medium text-slate-800">{date}</div>
                    {time && <div className="text-[11px] text-slate-400">{time}</div>}
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`${BADGE_CLASS} ${actorTypeBadgeClass(actorTypeLabel(log))}`}>
                        {humanizeToken(actorTypeLabel(log))}
                      </span>
                      {actorName ? (
                        <span className="text-slate-600">{actorName}</span>
                      ) : null}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    <span className={`${BADGE_CLASS} ${actionBadgeClass(actionLabel(log))}`}>
                      {actionLabel(log)}
                    </span>
                  </td>

                  <td className="px-3 py-3">
                    <div className="flex flex-col items-start gap-1">
                      <span className={`${BADGE_CLASS} ${targetTypeBadgeClass(targetTypeLabel(log))}`}>
                        {humanizeToken(targetTypeLabel(log))}
                      </span>
                      {targetName ? (
                        <span className="text-slate-600">{targetName}</span>
                      ) : null}
                    </div>
                  </td>

                  <td className="px-3 py-3">
                    {details.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {details.map(([label, value]) => (
                          <span
                            key={label}
                            className="inline-flex items-center gap-1 rounded-md bg-slate-50 px-2 py-0.5 text-[11px] ring-1 ring-inset ring-slate-200"
                          >
                            <span className="text-slate-400">{label}</span>
                            <span className="font-medium text-slate-700">{value}</span>
                          </span>
                        ))}
                      </div>
                    ) : (
                      <span className="text-slate-400">--</span>
                    )}
                  </td>

                  <td className="px-3 py-3 text-center">
                    <button
                      onClick={() => onView(log)}
                      className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-medium text-slate-600 shadow-sm transition hover:bg-slate-50 hover:text-slate-900"
                    >
                      <FontAwesomeIcon icon={faEye} />
                      View
                    </button>
                  </td>
                </tr>
              );
            })}

        </tbody>
      </table>
    </div>
  );
};

export default ActivityLogTable;
